import React from "react"
import { format, isThisWeek, startOfDay } from "date-fns"
import { pl } from "date-fns/locale"
import { cn } from "@/lib/utils"
import { MESSAGE_ROW_INSET } from "@/components/ui/message/rowInset"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

type DayMarkerProps = {
  date: Date
  /** Set inside the driver workspace: Polish dates, quiet line, wider inset. */
  isDriverRoomChat?: boolean
}

const DayMarker: React.FC<DayMarkerProps> = ({
  date,
  isDriverRoomChat = false,
}) => {
  const { t } = useTranslation()
  const formatDate = (date: Date) => {
    const startOfDayDate = startOfDay(date)
    // Legacy keeps Polish dates in driver rooms only; a chosen language
    // applies everywhere.
    const options = {
      locale: getDateFnsLocale(isDriverRoomChat ? pl : undefined),
    }

    if (isThisWeek(startOfDayDate, { weekStartsOn: 1 })) {
      return format(
        startOfDayDate,
        isDriverRoomChat ? "EEEE d MMMM" : t("app.dates.dayMarkerThisWeek"),
        options,
      )
    } else {
      return format(
        startOfDayDate,
        isDriverRoomChat ? "d MMMM" : t("app.dates.dayMarker"),
        options,
      )
    }
  }

  // Outside the driver workspace the marker stays the pill it has always been.
  if (!isDriverRoomChat) {
    return (
      <div className="my-4 flex items-center justify-center">
        <div className="flex h-9 items-center rounded-[8px] border border-input bg-white px-4 py-1 text-sm font-medium leading-normal text-foreground shadow-xs dark:bg-gray-700 dark:bg-input/30 dark:text-gray-300">
          {formatDate(date)}
        </div>
      </div>
    )
  }

  // In the workspace it is a quiet line, aligned with the message content.
  return (
    <div
      className={cn(
        MESSAGE_ROW_INSET,
        "my-6 text-left text-[11px] font-medium tracking-wide text-muted-foreground",
      )}
    >
      {formatDate(date)}
    </div>
  )
}

export default DayMarker
